"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FullServiceAdministration() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [activeTab, setActiveTab] = useState("investment");
  const tabs = [{
    value: "investment",
    label: "Property Admin",
    heading: "Institutional oversight for private assets.",
    description: "We provide rigorous administrative management for high-value properties and fractional interests. From legal compliance with PROFECO to SAT tax registration, we ensure your asset operates with the precision of a corporate entity.",
    pillars: ["PROFECO & SAT legal compliance", "Transparent owner reporting suites", "HOA and fractional coordination", "Cross-border transaction security", "Zero upfront cost assessment"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/69dae293-bb58-479a-a80c-0db6ecffbaa2.png",
      srcset: "",
      sizes: "",
      alt: "Architectural detail of a luxury Cabo property managed by IMI"
    }
  }, {
    value: "retirement",
    label: "Rental Yield",
    heading: "Optimizing revenue through strategic leasing.",
    description: "Your property is an investment that should perform. Our rental optimization strategy combines dynamic pricing with high-touch hospitality to maximize occupancy and yield while preserving the long-term value of the estate.",
    pillars: ["Dynamic market pricing models", "Global marketing & distribution", "Guest vetting and screening", "Revenue performance analytics"],
    image: {
      src: "https://imielite.com/wp-content/uploads/2026/06/318ec5f2-9721-41ba-9057-e8a1ae5b82c3.webp?v=u5863gb7u1",
      srcset: "",
      sizes: "",
      alt: "Luxury villa interior showcasing high-end rental potential"
    }
  }, {
    value: "tax",
    label: "Exit Strategy",
    heading: "A clear path to asset liberation.",
    description: "Exiting a timeshare or fractional interest requires legal precision and transparency. We manage the entire transfer process with zero upfront costs, ensuring a compliant and permanent release from future liabilities.",
    pillars: ["Zero upfront cost assessment", "Legal transfer fee transparency", "Contractual liability release", "Step-by-step process tracking"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/updated_contract_2.webp",
      srcset: "",
      sizes: "",
      alt: "Close-up of legal documents and keys representing a successful exit"
    }
  }, {
    value: "estate",
    label: "Hospitality",
    heading: "Curated experiences, flawlessly executed.",
    description: "Luxury is defined by the details. Our concierge team coordinates everything from private chefs and marine charters to location scouting, ensuring every guest experience reflects the elite status of the property.",
    pillars: ["Private chef & culinary services", "Marine charters & yachting", "VIP transport & logistics", "Photography & location scouting"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/chef_full_scene_realistic-1.webp",
      srcset: "",
      sizes: "",
      alt: "Private yacht on the Cabo horizon representing luxury concierge"
    }
  }, {
    value: "risk",
    label: "Maintenance",
    heading: "Preservation as a core discipline.",
    description: "Preventative maintenance is the foundation of asset longevity. We oversee a network of vetted contractors to ensure your property remains in pristine condition, protecting both its aesthetic appeal and market value.",
    pillars: ["Preventative maintenance schedules", "Emergency repair coordination", "Vendor vetting & management", "Regular site inspections"],
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/luxury_pool_maintenance_check.png",
      srcset: "",
      sizes: "",
      alt: "Clean architectural lines of a well-maintained luxury estate"
    }
  }];
  return <section data-section-id="751" id="full-service-administration" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs tracking-[0.2em] uppercase font-semibold" style={{
            color: "var(--primary)"
          }}>
              Management Pillars
            </span>
            <span className="font-mono text-xs tracking-[0.2em] font-semibold" style={{
            color: "var(--primary)"
          }}>
              02
            </span>
          </div>

          <h2 className="font-default font-semibold text-foreground mt-6 max-w-3xl leading-[1.2] border-l-4 pl-6" style={{
          fontSize: "clamp(2rem, 5vw, 3.5rem)",
          borderColor: "var(--primary)"
        }}>
            Architects of Asset Liberation.
          </h2>

          <p className="font-serif italic text-slate-muted mt-8 leading-relaxed" style={{
          fontSize: "1.25rem",
          maxWidth: "640px"
        }}>
            Elite management is the integration of legal compliance, 
            operational excellence, and luxury hospitality — where every 
            detail is managed as one coherent whole.
          </p>
        </div>

        <div className="mt-20">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="border-b border-border">
              <TabsList className="bg-transparent p-0 h-auto w-full flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible justify-start gap-0">
                {tabs.map((tab, i) => <TabsTrigger key={tab.value} value={tab.value} data-index={i} className="relative bg-transparent border-0 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 md:px-8 py-5 text-sm font-semibold tracking-widest uppercase text-slate-muted data-[state=active]:text-foreground transition-colors duration-300 whitespace-nowrap first:pl-0 rounded-md">
                    {tab.label}
                    {activeTab === tab.value && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] transition-all duration-400" style={{
                  backgroundColor: "var(--primary)"
                }} />}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {tabs.map((tab, i) => <TabsContent key={tab.value} value={tab.value} data-index={i} className="mt-16 focus-visible:outline-none data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-3 data-[state=active]:duration-500">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                  <div className="lg:col-span-6">
                    <h3 className="font-default font-medium text-foreground leading-[1.3]" style={{
                  fontSize: "clamp(1.25rem, 2vw, 1.75rem)"
                }}>
                      {tab.heading}
                    </h3>

                    <p className="font-default text-slate-muted mt-6" style={{
                  fontSize: "1rem",
                  lineHeight: "1.5"
                }}>
                      {tab.description}
                    </p>

                    <ul className="mt-10 space-y-4">
                      {tab.pillars.map((pillar, idx) => <li key={idx} data-index={idx} className="flex items-start gap-4">
                          <span className="mt-2 inline-block h-2 w-2 shrink-0" style={{
                      backgroundColor: "var(--primary)"
                    }} aria-hidden="true" />
                          <span className="text-base text-foreground font-default">
                            {pillar}
                          </span>
                        </li>)}
                    </ul>

                    <Link to="/ContactPage" className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold tracking-widest uppercase transition-opacity hover:opacity-80" style={{
                  color: "var(--primary)"
                }}>
                      <span className="border-b border-current pb-0.5">
                        Request an Asset Assessment
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>

                  <div className="lg:col-span-6">
                    <div className="relative overflow-hidden rounded-md border border-border" style={{
                  aspectRatio: "4 / 5"
                }}>
                      <img src={tab.image.src} data-wvc-srcset={tab.image.srcset} data-wvc-sizes={tab.image.sizes} alt={tab.image.alt} loading="lazy" decoding="async" className={`h-full w-full object-cover grayscale hover:grayscale-0 transition-all duration-700 ${i === 1 ? "object-right" : ""}`} />
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}